import { memo } from 'react';
import type { ButtonHTMLAttributes, ElementType, ReactNode } from 'react';

import classNames from 'classnames';

import type { PolymorphicPropsWithRef } from '@/shared/types';
import type { OpdsStyleProps } from '@/shared/ui/Box';
import { Loading } from '@/shared/ui/Loading';
import { Text } from '@/shared/ui/Text';
import { UnstyledButton } from '@/shared/ui/UnstyledButton';

import styles from './Button.module.css';

type ButtonElementProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'prefix'>;

type ButtonSize = 'small' | 'regular' | 'medium' | 'large';

export type ButtonColor = 'standard' | 'primary' | 'inverse' | 'critical';
export type ButtonVariant = 'filled' | 'outlined' | 'tonal' | 'text' | 'critical';

export type ButtonProps = ButtonElementProps &
  OpdsStyleProps & {
    prefix?: ReactNode;
    suffix?: ReactNode;

    /** @default 'regular' */
    size?: ButtonSize;

    /**
     * 선택된 상태
     *
     *  @default false
     */
    selected?: boolean;
    fullWidth?: boolean;
    color?: ButtonColor;
    variant?: ButtonVariant;
    children?: ReactNode;

    /**
     * 로딩 상태
     *
     * @default false
     */
    loading?: boolean;
  };

export type PolymorphicButtonProps<C extends ElementType = 'button'> = PolymorphicPropsWithRef<C, ButtonProps>;

const Button = <C extends ElementType = 'button'>(props: PolymorphicButtonProps<C>) => {
  const {
    className,
    prefix,
    suffix,
    selected,
    fullWidth,
    children,
    as = 'button',
    ref,
    color = 'standard',
    variant = 'filled',
    size = 'regular',
    loading = false,
    disabled,
    ...rest
  } = props;

  let buttonVariant = variant;
  let buttonColor = color;

  if (color === 'critical') {
    if (variant !== 'outlined') {
      buttonVariant = 'critical';
    }
  } else if (variant === 'critical') {
    buttonColor = 'critical';
  }

  // text variant일 경우 selected 상태일때 title로 변경
  const textVariant = variant === 'text' && selected ? 'title' : 'body';
  const textSize = size === 'large' ? (textVariant === 'title' ? 'medium' : 'large') : 'small';

  const loadingSize = size === 'large' ? 'medium' : 'small';

  return (
    <UnstyledButton
      as={as as ElementType}
      className={classNames(className, styles.button)}
      ref={ref}
      data-variant={buttonVariant}
      data-selected={selected}
      data-full-width={fullWidth}
      data-color={buttonColor}
      data-size={size}
      data-loading={loading}
      data-testid="opds-button-root"
      disabled={disabled || loading}
      {...rest}>
      {prefix}
      <Text variant={textVariant} size={textSize} className={styles.text}>
        {children}
      </Text>
      {loading && (
        <div className={styles.loadingWrapper}>
          <Loading size={loadingSize} mode="indeterminate" color="standard" />
        </div>
      )}
      {suffix}
    </UnstyledButton>
  );
};

Button.displayName = 'Button';

export default memo(Button) as typeof Button;
